import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, CheckCircle2, AlertTriangle, Lock, Trash2, Signature,
} from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../lib/constants';

const LEVEL_TONE = {
  bajo:    { color: '#065F46', bg: '#ECFDF5', label: 'Bajo' },
  medio:   { color: '#B45309', bg: '#FFFBEB', label: 'Medio' },
  alto:    { color: '#B91C1C', bg: '#FEF2F2', label: 'Alto' },
  critico: { color: '#7F1D1D', bg: '#FEE2E2', label: 'Crítico' },
};

const STATUS_LABEL = {
  borrador: 'Borrador', en_revision: 'En revisión',
  aprobada: 'Aprobada', aprobada_con_condiciones: 'Aprobada c/ condiciones',
  requiere_consulta: 'Consulta previa Agencia', vencida: 'Vencida',
};

export default function DpiaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState(null);
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState({ decision: 'aprobada', notes: '' });

  const load = useCallback(async () => {
    try {
      const [dpia, m, meRes] = await Promise.all([
        api.get(`/dpia/${id}`), api.get('/dpia/meta'), api.get('/auth/me'),
      ]);
      setD(dpia.data); setMeta(m.data); setMe(meRes.data.user);
    } catch { toast.error('No se pudo cargar la DPIA'); nav('/app/dpia'); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!d || !meta) return <div className="p-6 text-sm text-[#6B7280]">Cargando…</div>;

  const isApproved = ['aprobada', 'aprobada_con_condiciones'].includes(d.status);
  const readOnly = isApproved;
  const canApprove = me && ['admin', 'compliance_officer'].includes(me.role);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/dpia/${id}`, payload);
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const setField = (k) => (e) => patch({ [k]: e.target.value });

  const toggleRisk = (r) => {
    if (readOnly) return;
    const existing = (d.risks || []).find((x) => x.key === r.key);
    let next;
    if (existing) next = d.risks.filter((x) => x.key !== r.key);
    else next = [...(d.risks || []), { ...r, probability: 3, impact: 3, level: 'medio' }];
    patch({ risks: next });
  };

  const updateRisk = (key, field, value) => {
    const next = d.risks.map((r) => r.key === key ? { ...r, [field]: Number(value) } : r);
    patch({ risks: next });
  };

  const toggleMitigation = (kind, value) => {
    if (readOnly) return;
    const field = `mitigations_${kind}`;
    const current = d[field] || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    patch({ [field]: next });
  };

  const submitDecision = async () => {
    try {
      const { data } = await api.post(`/dpia/${id}/decision`, decision);
      setD(data); setDecisionOpen(false);
      toast.success('Decisión registrada');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const residualTone = LEVEL_TONE[d.residual_risk_level] || LEVEL_TONE.medio;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="dpia-detail">
      <button onClick={() => nav('/app/dpia')} className="text-[12px] text-[#6B7280] hover:text-[#143A34] inline-flex items-center gap-1">
        <ArrowLeft size={13} /> Volver a DPIAs
      </button>

      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">Ref {d.id.slice(0, 8)}</div>
        <h1 className="text-[22px] font-semibold text-[#1C2329] mt-1">{d.title}</h1>
        {d.activity_name && (
          <div className="text-[13px] text-[#6B7280] mt-1">RAT vinculado: <b>{d.activity_name}</b></div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Estado" value={STATUS_LABEL[d.status]} color={isApproved ? '#065F46' : '#374151'} bg={isApproved ? '#ECFDF5' : '#F3F4F6'} border={isApproved ? '#A7F3D0' : '#E5E7EB'} />
        <StatCard label="Riesgo residual" value={residualTone.label.toUpperCase()} sub={`${(d.risks || []).length} riesgo(s) evaluado(s)`}
          color={residualTone.color} bg={residualTone.bg} border={residualTone.color + '55'} highlight />
        <StatCard label={d.approved_at ? 'Próxima revisión' : 'Firmada por'} value={d.approved_at ? formatDate(d.next_review_at) : (d.approved_by_email || '—')}
          sub={d.approved_at ? `Firmada ${formatDate(d.approved_at)}` : 'Pendiente de decisión DPO'}
          color="#0F2A26" bg="#F0F7F5" border="#B7D8CE" />
      </div>

      {readOnly && (
        <div className="border border-[#D1FAE5] bg-[#ECFDF5] px-4 py-2.5 flex items-center gap-2 text-[12px] text-[#065F46]">
          <Lock size={14} /> Esta DPIA fue firmada por {d.approved_by_email}. Para modificarla debe crear una revisión.
        </div>
      )}

      {/* Triggers detectados */}
      {d.triggers?.length > 0 && (
        <section className="border border-[#FDE68A] bg-[#FFFBEB] p-4">
          <div className="text-[11.5px] uppercase tracking-wider text-[#B45309] font-semibold mb-2 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Triggers de alto riesgo detectados
          </div>
          <div className="flex flex-wrap gap-1.5">
            {d.triggers.map((t) => (
              <span key={t} className="text-[11px] px-2 py-1 bg-white border border-[#FDE68A] text-[#92400E]">
                {meta.triggers[t] || t}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Paso 2 · Necesidad y proporcionalidad */}
      <section className="space-y-3">
        <h2 className="text-[14px] font-semibold text-[#1C2329]">1. Necesidad y proporcionalidad</h2>
        <Field label="¿Por qué es necesario tratar estos datos?">
          <textarea className="pv-input" rows={2} value={d.necessity || ''} onChange={setField('necessity')} disabled={readOnly}
            placeholder="Ej: Para prestar el servicio contratado y cumplir con obligaciones legales tributarias." />
        </Field>
        <Field label="¿Existe una forma menos invasiva de lograr el mismo objetivo?">
          <textarea className="pv-input" rows={2} value={d.less_invasive_alternative || ''} onChange={setField('less_invasive_alternative')} disabled={readOnly}
            placeholder="Ej: Se evaluó pedir menos datos pero es insuficiente para verificar identidad." />
        </Field>
        <Field label="¿Cuánto tiempo se mantienen y por qué?">
          <textarea className="pv-input" rows={2} value={d.retention_justification || ''} onChange={setField('retention_justification')} disabled={readOnly}
            placeholder="Ej: 6 años según obligación tributaria (art. 200 Código Tributario), luego se destruyen." />
        </Field>
      </section>

      {/* Paso 3 · Riesgos */}
      <section className="space-y-2">
        <h2 className="text-[14px] font-semibold text-[#1C2329]">2. Identificación de riesgos</h2>
        <p className="text-[11.5px] text-[#6B7280]">Marque los riesgos aplicables. Por cada uno, ajuste probabilidad × impacto → Priviax calcula el nivel.</p>
        <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
          {meta.risk_catalog.map((r) => {
            const active = (d.risks || []).find((x) => x.key === r.key);
            return (
              <div key={r.key} className={`px-3 py-2.5 ${active ? 'bg-[#F0F7F5]' : 'bg-white'}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!active} onChange={() => toggleRisk(r)} disabled={readOnly} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[12.5px] font-medium text-[#1C2329]">{r.label}</div>
                    <div className="text-[11px] text-[#6B7280]">{r.description}</div>
                  </div>
                </label>
                {active && (
                  <div className="ml-6 mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Probabilidad (1-5)</label>
                      <input type="range" min="1" max="5" value={active.probability} disabled={readOnly}
                        onChange={(e) => updateRisk(r.key, 'probability', e.target.value)} className="w-full" />
                      <div className="text-[10.5px] text-[#374151]">{active.probability} / 5</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Impacto (1-5)</label>
                      <input type="range" min="1" max="5" value={active.impact} disabled={readOnly}
                        onChange={(e) => updateRisk(r.key, 'impact', e.target.value)} className="w-full" />
                      <div className="text-[10.5px] text-[#374151]">{active.impact} / 5</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Nivel</div>
                      <div className="inline-block text-[13px] font-bold px-2 py-1 mt-1"
                        style={{ color: LEVEL_TONE[active.level].color, backgroundColor: LEVEL_TONE[active.level].bg }}>
                        {LEVEL_TONE[active.level].label}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Paso 4 · Medidas mitigadoras */}
      <section className="space-y-3">
        <h2 className="text-[14px] font-semibold text-[#1C2329]">3. Medidas mitigadoras</h2>
        {['tecnicas', 'organizativas', 'contractuales'].map((kind) => (
          <div key={kind}>
            <div className="text-[11.5px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">
              {kind === 'tecnicas' ? 'Técnicas' : kind === 'organizativas' ? 'Organizacionales' : 'Contractuales'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {meta.mitigation_catalog[kind].map((m) => {
                const on = (d[`mitigations_${kind}`] || []).includes(m);
                return (
                  <label key={m} className={`flex items-center gap-2 px-2 py-1.5 border cursor-pointer text-[12px] ${on ? 'bg-[#F0F7F5] border-[#143A34]' : 'bg-white border-[#E5E7EB]'} ${readOnly ? 'cursor-not-allowed' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleMitigation(kind, m)} disabled={readOnly} />
                    <span className="text-[#374151]">{m}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Paso 5 · Decisión DPO */}
      <section className="border-2 border-[#143A34] bg-[#F0F7F5] p-4">
        <h2 className="text-[14px] font-semibold text-[#0F2A26] flex items-center gap-2">
          <Signature size={14} /> 4. Decisión del DPO
        </h2>
        {isApproved || d.status === 'requiere_consulta' ? (
          <div className="mt-2 space-y-1">
            <div className="text-[12.5px] text-[#065F46]">
              <CheckCircle2 size={12} className="inline mr-1" />
              {STATUS_LABEL[d.status]} el {formatDate(d.approved_at)} por <b>{d.approved_by_email}</b>
            </div>
            {d.dpo_decision_notes && (
              <div className="text-[11.5px] text-[#374151] bg-white border-l-2 border-[#10B981] pl-2 py-1 mt-2">
                <b>Notas del DPO:</b> {d.dpo_decision_notes}
              </div>
            )}
          </div>
        ) : canApprove ? (
          <div className="mt-3 space-y-2">
            <p className="text-[12px] text-[#374151]">
              Como {me.role === 'admin' ? 'administrador' : 'compliance officer'}, usted puede firmar esta DPIA. Es una decisión con efecto legal.
            </p>
            <button className="px-4 py-2 bg-[#143A34] text-white text-[13px] font-medium"
              onClick={() => setDecisionOpen(true)} disabled={!(d.risks || []).length}
              data-testid="dpia-sign">
              <Signature size={13} className="inline mr-1" /> Firmar decisión
            </button>
            {!(d.risks || []).length && (
              <p className="text-[11px] text-[#B45309]">Registre al menos un riesgo antes de firmar.</p>
            )}
          </div>
        ) : (
          <p className="mt-2 text-[12px] text-[#374151]">Solo el DPO (rol admin o compliance officer) puede firmar esta DPIA.</p>
        )}
      </section>

      {/* Modal decisión */}
      {decisionOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDecisionOpen(false)}>
          <div className="bg-white max-w-md w-full p-5 border-t-4 border-[#143A34]" onClick={(e) => e.stopPropagation()} data-testid="dpia-decision-modal">
            <div className="text-[15px] font-semibold text-[#1C2329]">Firma del DPO</div>
            <p className="text-[11.5px] text-[#6B7280] mt-1">Elija la decisión y agregue notas si corresponde.</p>
            <div className="space-y-2 mt-3">
              {[
                { k: 'aprobada', label: 'Aprobada · el tratamiento puede realizarse con las medidas actuales' },
                { k: 'aprobada_con_condiciones', label: 'Aprobada con condiciones · implementar medidas adicionales antes' },
                { k: 'requiere_consulta', label: 'Requiere consulta previa a la Agencia (riesgo residual alto)' },
              ].map((opt) => (
                <label key={opt.k} className={`flex gap-2 p-2 border cursor-pointer text-[12px] ${decision.decision === opt.k ? 'border-[#143A34] bg-[#F0F7F5]' : 'border-[#E5E7EB]'}`}>
                  <input type="radio" checked={decision.decision === opt.k} onChange={() => setDecision({ ...decision, decision: opt.k })} className="mt-0.5" />
                  <span className="text-[#374151]">{opt.label}</span>
                </label>
              ))}
            </div>
            <textarea rows={3} className="pv-input mt-3 text-[12px]"
              placeholder={decision.decision === 'aprobada_con_condiciones' ? 'Condiciones a cumplir (obligatorio)' : 'Notas del DPO (opcional)'}
              value={decision.notes} onChange={(e) => setDecision({ ...decision, notes: e.target.value })}
              data-testid="dpia-decision-notes" />
            <div className="flex justify-end gap-2 mt-3">
              <button className="pv-btn-secondary" onClick={() => setDecisionOpen(false)}>Cancelar</button>
              <button className="px-3 py-1.5 bg-[#143A34] text-white text-[12.5px] font-medium"
                onClick={submitDecision} data-testid="dpia-decision-submit">
                <CheckCircle2 size={12} className="inline mr-1" /> Firmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, bg, border, highlight }) {
  return (
    <div className="border-2 px-3 py-2.5" style={{ backgroundColor: bg, borderColor: border }}>
      <div className="text-[9.5px] uppercase tracking-wider font-medium" style={{ color, opacity: 0.75 }}>{label}</div>
      <div className={`font-semibold leading-tight tracking-tight ${highlight ? 'text-[20px]' : 'text-[15px]'}`} style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-[10.5px] mt-0.5" style={{ color, opacity: 0.7 }}>{sub}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[12px] font-medium text-[#374151] block mb-1">{label}</label>{children}</div>;
}
